/* eslint-disable react/jsx-no-useless-fragment */
import React from 'react';
import { useAuthContext } from '../context';
import { useRouter } from 'next/router';
import { Redirect } from './redirect';
import { useIsEmployerAnAdmin, useEmployerUserInformationLoadingState } from '../hook';
import { consumeLoginTokenFromUrl } from '../utils/login-token-url';
import { internalPath, resolveAndReportLoginDestination } from '../utils/login-destination';

export type AuthGuardProps = {
  children: React.ReactNode;
  loader: React.ReactNode;
};

export const COLLABORATOR_ROLE_KEYS = {
  ADMIN: 'admin_employer_user',
  BASE: 'base_employer_user',
} as const;

const privatePaths = [
  '/worker/',
  '/employer/',
  '/referral/',
  '/impostazioni',
  '/profilo',
  '/candidature',
  '/porta-un-locale',
  '/v2/employer/',
];
const redirectLocation = '/login';

export const AuthGuard = ({ children, loader }: AuthGuardProps): React.ReactElement<any> => {
  const router = useRouter();
  const authContext = useAuthContext();
  const isAdminEmployer = useIsEmployerAnAdmin();
  const isEmployerInfoLoading = useEmployerUserInformationLoadingState();

  if (authContext) {
    const { user: userData, isReady, jwt, isAuthenticated, logoutRedirect } = authContext;
    const { query, asPath } = router;
    const currentPath = router.asPath;
    // Same source the login screen reads: the magic-link token is taken out of the address
    // bar before Next parses it, so `router.query` no longer carries it. Without this the
    // guard would send an already-signed-in visitor away from /login, and a magic link for
    // a different account would never get the chance to switch user.
    const token = consumeLoginTokenFromUrl() || router?.query?.['token']?.toString() || '';

    // Check if the URL includes a "?"
    const indexOfQuestionMark = currentPath.indexOf('?');

    // Determine the base path based on whether there's a query string
    const basepath = indexOfQuestionMark >= 0 ? currentPath.slice(0, indexOfQuestionMark) : currentPath;
    let queryParams = '';
    if (query) {
      queryParams = Object.keys(query)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key] as string)}`)
        .join('&');
    }
    const currentUrl = `${basepath}${queryParams ? `?${queryParams}` : ''}`;
    const isPrivate = privatePaths.filter((privatePath) => basepath.startsWith(privatePath)).length > 0;
    const isEmployer = !!userData && (userData.type === 'employer' || userData?.type === 'employer_contact');

    if (logoutRedirect && !isAuthenticated) {
      return <Redirect to={isPrivate ? '/' : currentUrl} refresh={true} />;
    }

    if ((!isReady || (isEmployer && isEmployerInfoLoading)) && isPrivate)
      return <ScreenSpinnerWrapper>{loader}</ScreenSpinnerWrapper>;

    // check if private route
    if (isPrivate) {
      // private routes
      if (!jwt && isReady && !logoutRedirect) return <Redirect to={`${redirectLocation}?redirect_url=${currentUrl}`} />;
      if (jwt) {
        if (asPath.startsWith('/worker/') || asPath.startsWith('/profilo') || asPath.startsWith('/candidature')) {
          if (userData && (userData.type === 'employer' || userData?.type === 'employer_contact'))
            return <Redirect to="/v2/employer/homepage" />;
          else if (userData && userData.type === 'ambassador') return <Redirect to="/challenge-wom/posizioni" />;
          else if (userData && userData.type === 'lead') return <Redirect to="/candidati" />;
        }
        if (asPath.startsWith('/employer/') || asPath.startsWith('/v2/employer/')) {
          if (userData && userData.type === 'worker') return <Redirect to="/profilo" />;
          else if (userData && userData.type === 'ambassador') return <Redirect to="/challenge-wom/posizioni" />;
          else if (userData && userData.type === 'lead') return <Redirect to="/" />;
          else if (userData && asPath.includes('payments-and-invoices') && !isAdminEmployer) return <Redirect to="/" />;
          else return <>{children}</>;
        }
        // if (asPath.startsWith('/profilo') && userData && userData.type === 'lead') return <Redirect to="/candidati" />;
        return <>{children}</>;
      }
    } else {
      // An employer who lands on the public homepage goes to their dashboard. That is not a
      // login arrival, so no destination is involved.
      if (asPath === '/' && isEmployer) return <Redirect to="/v2/employer/homepage" />;

      // A bare `/login` **is** a login arrival — the login screen has just taken the spent
      // token out of the address bar, and on a link with no destination it leaves nothing
      // behind. This used to send every employer to the dashboard before anything looked for a
      // destination, which is the same defect as the ladder below and fired one branch earlier.
      if (asPath === '/login' && jwt && isReady && isAuthenticated && !token)
        return <Redirect to={destinationAfterLogin(userData?.type, query)} />;
    }

    // public routes
    if (asPath.startsWith('/reset-password') && jwt && isReady) return <Redirect to="/profilo" />;
    if (asPath.startsWith('/login') || asPath.startsWith('/signup')) {
      if (jwt && isReady && isAuthenticated && !token) {
        return <Redirect to={destinationAfterLogin(userData?.type, query)} />;
      }
    }

    return <>{children}</>;
  }
  return <></>;
};

const ScreenSpinnerWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={`w-screen h-screen flex items-center justify-center ${className || ''}`} {...props} />
);

/** Where each kind of user ends up when the link named nowhere in particular. */
const homeFor = (userType: string | undefined): string => {
  if (userType === 'employer' || userType === 'employer_contact') return '/v2/employer/homepage';
  if (userType === 'worker') return '/profilo';
  if (userType === 'ambassador') return '/challenge-wom/posizioni';
  return '/';
};

/**
 * Where somebody who is already signed in and sitting on `/login` should go.
 *
 * **The destination is checked before the user type, and that is the change.** The ladder used
 * to read "if employer, go to the employer homepage; else if there is a destination, go
 * there", so for an `employer` or `employer_contact` the destination branch was unreachable —
 * the backend computed it, the e-mail carried it, and the guard ignored it. Four emitters were
 * written by someone who believed otherwise: the recommended-candidates digest, the
 * applications-without-feedback mail, the referral invite and the spontaneous-application
 * notification all name a page and all landed on the homepage instead. That has been true
 * since 28 March 2025 and was never a decision, just the order two commits happened to land in.
 *
 * Sending a user type somewhere it may not go is not this function's problem to solve, and
 * deliberately so: the private-route branch above already bounces a worker off `/employer/*`
 * and an employer off `/profilo`, so an impossible destination costs one extra hop and needs no
 * table here that could go stale as routes are added.
 *
 * `redirect_url` and `redirectTo` are the same thing under two names — the second is what the
 * job-favourite modal writes — so both are read, and **both go through `internalPath`**. The
 * reading side used to push whatever the query held, which meant the sanitising done on the
 * write side guarded the pipe and not the tap: a pasted or crafted `/login?redirect_url=…`
 * opened by somebody already signed in bypassed it entirely.
 */
const destinationAfterLogin = (
  userType: string | undefined,
  query: Record<string, string | string[] | undefined>
): string => {
  const home = homeFor(userType);
  const { path } = resolveAndReportLoginDestination(undefined, 'auth_guard', home);

  return path ?? internalPath(query?.['redirectTo']) ?? home;
};
